// ── PresumedDeductionCard.js — "이번 분기 환급 추정 X원" 큰 wow 카드 ─────
// 거래장부 탭 상단에 배치. 매일 갱신되는 retention hook.

import React from 'react';
import {
  pickPresumedRate, calcPresumedDeduction, evaluatePresumedLimit,
} from '../data/taxConstants';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');

const STATUS_STYLES = {
  unknown: {
    bgGradient: '#F4F4F5',
    border: '#d1d5db',
    accent: '#6b7280',
    iconBg: '#fff',
    statusLabel: '한도 미상',
    statusColor: '#6b7280',
    statusBg: '#fff',
  },
  safe: {
    bgGradient: '#FAFAFA',
    border: '#FED7AA',
    accent: '#EA580C',
    iconBg: '#fff',
    statusLabel: '한도 안전',
    statusColor: '#EA580C',
    statusBg: '#E4E4E7',
  },
  warning: {
    bgGradient: '#F4F4F5',
    border: '#D4D4D8',
    accent: '#EA580C',
    iconBg: '#fff',
    statusLabel: '한도 임박',
    statusColor: '#EA580C',
    statusBg: '#fde68a',
  },
  over: {
    bgGradient: '#F4F4F5',
    border: '#fca5a5',
    accent: '#52525B',
    iconBg: '#fff',
    statusLabel: '한도 초과',
    statusColor: '#52525B',
    statusBg: '#E4E4E7',
  },
};

export default function PresumedDeductionCard({
  presumedSupply = 0,           // 의제매입대상 매입가 누적 (이번 분기)
  annualRevenue = null,         // 연 매출 추정 (한도 계산용)
  industry = 'food',
  presumedTransactions = [],    // 의제매입 마킹된 거래 리스트 (개수 표시용)
  onOpenRevenueInput,           // 매출 입력 모달 열기
  onShowDetails,                // 상세 보기
  periodLabel = '',
}) {
  const rate = pickPresumedRate({ industry, annualRevenue });
  const refundEst = calcPresumedDeduction(presumedSupply, rate);
  const limitEval = evaluatePresumedLimit({ presumedSupply, annualRevenue, industry });
  const style = STATUS_STYLES[limitEval.status];

  const hasData = presumedSupply > 0;

  return (
    <div style={{
      borderRadius: 14,
      padding: 20,
      background: hasData ? style.bgGradient : '#F9FAFB',
      border: `1.5px solid ${hasData ? style.border : '#e5e7eb'}`,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* 우측 상단 status badge */}
      {hasData && (
        <div style={{
          position: 'absolute', top: 14, right: 14,
          padding: '4px 10px', borderRadius: 12,
          background: style.statusBg, color: style.statusColor,
          fontSize: 11, fontWeight: 700,
        }}>
          {style.statusLabel}
        </div>
      )}

      {/* 라벨 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>✨</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: hasData ? style.accent : '#888', letterSpacing: 0.3 }}>
          의제매입세액공제 환급 추정 · {periodLabel}
        </span>
      </div>

      {/* 메인 숫자 */}
      {hasData ? (
        <>
          <div style={{
            fontSize: 36, fontWeight: 900,
            color: style.accent, lineHeight: 1.1, marginBottom: 6,
            letterSpacing: -0.5,
          }}>
            약 {fmt(refundEst)}원
          </div>
          <div style={{ fontSize: 12, color: hasData ? style.accent : '#888', opacity: 0.85 }}>
            의제매입대상 매입가 {fmt(presumedSupply)}원 × {rate.label}
          </div>

          {/* 한도 진행 막대 */}
          {limitEval.status !== 'unknown' && (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4, color: style.accent }}>
                <span>한도 사용</span>
                <span>{fmt(limitEval.taken)}원 / {fmt(limitEval.limit)}원 ({Math.round(limitEval.ratio * 100)}%)</span>
              </div>
              <div style={{ background: '#fff', borderRadius: 5, height: 8, overflow: 'hidden', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)' }}>
                <div style={{
                  background: style.accent,
                  height: '100%',
                  width: `${Math.min(100, limitEval.ratio * 100)}%`,
                  transition: 'width .4s',
                }} />
              </div>
            </div>
          )}

          {/* 한도 미상 안내 */}
          {limitEval.status === 'unknown' && (
            <div style={{ marginTop: 12 }}>
              <button onClick={onOpenRevenueInput} type="button"
                style={{
                  padding: '6px 12px', border: '1px solid #FF6F0F',
                  background: '#fff', color: '#18181B',
                  borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>
                💡 매출 입력해서 한도 정확히 보기
              </button>
            </div>
          )}

          <div style={{
            marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${style.border}`,
            display: 'flex', gap: 12, fontSize: 11, color: style.accent, opacity: 0.85, flexWrap: 'wrap',
          }}>
            <span>대상 거래 {presumedTransactions.length}건</span>
            {onShowDetails && (
              <button onClick={onShowDetails} type="button"
                style={{ background: 'none', border: 'none', color: style.accent, cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: 0, textDecoration: 'underline' }}>
                내역 보기
              </button>
            )}
            <span style={{ marginLeft: 'auto' }}>※ 한도·증빙 검증 후 신고 시 적용</span>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#666', lineHeight: 1.4 }}>
            매입 거래에 면세 농축수산물이 있으면<br/>여기에 환급 추정액이 자동으로 뜹니다.
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>
            예: <span style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>한우</span> {' '}
            <span style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>야채</span> {' '}
            <span style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>생선</span> {' '}
            <span style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>쌀</span> {' '}
            등
          </div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
            적격증빙(세금계산서/카드/현금영수증)으로 매입한 경우만 자동 인식
          </div>
        </>
      )}
    </div>
  );
}
